import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { sortFlightLegs, withSortedFlightLegs } from '../../utils/flightLegs';

const BOOKING_BASE = '/bookings';

/* ──────────────────────────────────────────────────────────────────────────
   Bookings list (paginated) + detail
   ────────────────────────────────────────────────────────────────────────── */
export const useGetBookingStatusCounts = () => {
  const [data, setData] = useState({ All: 0, Draft: 0, Confirmed: 0, Pending: 0, Cancelled: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance
      .get(`${BOOKING_BASE}/status-counts`)
      .then((res) => { if (res.data?.success) setData(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
};

export const useGetBookings = (params = {}) => {
  const { page = 1, limit = 10, ...filters } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build the query: page/limit always, plus any non-empty filter param the API
  // understands (server-side filtering). Empty values are dropped.
  const queryParams = { page, limit };
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') queryParams[k] = v;
  });
  // Stable dependency so the effect only refetches when something actually changes
  const paramsKey = JSON.stringify(queryParams);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(BOOKING_BASE, { params: JSON.parse(paramsKey) });
      const res = response.data;
      if (res?.success) {
        setData((res.data || []).map(withSortedFlightLegs));
        if (res.pagination) setPagination(res.pagination);
      } else {
        setError(res?.message || 'Failed to load bookings');
        setData([]);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load bookings';
      setError(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  return { data, pagination, loading, error, refetch: fetchBookings };
};

/**
 * Soft-delete a booking (Super Admin only).
 * Backend DELETE /:id sets deletedAt on the paranoid Booking model, so the
 * record is hidden from all lists but remains recoverable in the database.
 */
export const useDeleteBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}`);
      const res = response.data;
      if (res?.success) return { success: true };
      return { success: false, message: res?.message || 'Failed to delete booking', code: res?.code };
    } catch (err) {
      const data = err.response?.data;
      const detail = data?.error && data.error !== data?.message ? `: ${data.error}` : '';
      const msg = (data?.message || err.message || 'Failed to delete booking') + detail;
      setError(msg);
      return { success: false, message: msg, code: data?.code };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useGetBookingDetail = (bookingId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBooking = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      // Cache-bust: the booking detail must always reflect the freshest state after
      // a mutation (e.g. removing an SSR). Without this, an OVH/proxy-cached response
      // can return stale data — the SSR keeps showing in the Pricing tab even though
      // the backend already removed it and recalculated the totals.
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      const res = response.data;
      if (res?.success) {
        // Legs are persisted chronologically, but bookings saved before that was
        // enforced can still come back grouped by direction — order them here so
        // every tab reading this booking shows the legs in the order they're flown.
        setData(withSortedFlightLegs(res.data));
      } else {
        setError(res?.message || 'Failed to load booking');
        setData(null);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load booking';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  return { data, loading, error, refetch: fetchBooking };
};

export const useGetBookingActivity = (bookingId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivity = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/activity`);
      const res = response.data;
      if (res?.success) {
        setData(res.data || []);
      } else {
        setError(res?.message || 'Failed to load activity');
        setData([]);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load activity';
      setError(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  return { data, loading, error, refetch: fetchActivity };
};

/**
 * Sync booking's customerName/customerEmail/customerPhone snapshot after a
 * customer profile edit, and log an activity entry for the booking.
 */
export const useSyncCustomerSnapshot = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingId, body = {}) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BOOKING_BASE}/${bookingId}/sync-customer`, body);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to sync customer snapshot' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to sync customer snapshot';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useChangeBookingCustomer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingId, customerId, customerType) => {
    if (!bookingId || !customerId || !customerType) {
      return { success: false, message: 'Booking ID, customer ID, and customer type are required' };
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}`, { customerId, customerType });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, warning: res.warning };
      return { success: false, message: res?.message || 'Failed to change customer' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to change customer';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/* ──────────────────────────────────────────────────────────────────────────
   Booking notes — office (internal) + invoice (customer-facing) rich text,
   plus OVH image upload for embedded thumbnails.
   ────────────────────────────────────────────────────────────────────────── */
export const useUpdateBookingNotes = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingId, { officeNotes, invoiceNotes }) => {
    if (!bookingId) return { success: false, message: 'Booking ID is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}`, { officeNotes, invoiceNotes });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, warning: res.warning };
      return { success: false, message: res?.message || 'Failed to save notes' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save notes';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useUploadBookingNoteImage = () => {
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (bookingId, file) => {
    if (!bookingId || !file) return { success: false, message: 'Booking ID and file are required' };
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axiosInstance.post(
        `${BOOKING_BASE}/${bookingId}/notes/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const res = response.data;
      if (res?.success && res.data?.imageUrl) return { success: true, imageUrl: res.data.imageUrl };
      return { success: false, message: res?.message || 'Image upload failed' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Image upload failed';
      return { success: false, message: msg };
    } finally {
      setUploading(false);
    }
  }, []);

  return { upload, uploading };
};

export const useCancelBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, payload = {}) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BOOKING_BASE}/${bookingId}/cancel`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, warnings: res.data?.warnings || [] };
      return { success: false, message: res?.message || 'Failed to cancel booking' };
    } catch (err) {
      // Surface governance hard-blocks (HTTP 409: SUPPLIER_PAYMENTS_EXIST / VAT_FINALIZED)
      // and the "already cancelled" guard (HTTP 400) so the UI can render a precise,
      // non-generic panel instead of a plain error toast.
      const data = err.response?.data;
      const msg = data?.message || err.message || 'Failed to cancel booking';
      setError(msg);
      return {
        success: false,
        message: msg,
        status: err.response?.status,
        blockType: data?.blockType || null,
        rule: data?.rule || null,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/**
 * Cancel a single product/component within a booking (partial cancellation).
 * Backend POST /:id/products/:productId/cancel marks productStatus + serviceStatus
 * to 'Cancelled' and, when cancelAssignedTravelers is true, releases the
 * traveler↔product assignments for that product.
 */
export const useCancelBookingProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, payload = {}) => {
    if (!bookingId || !productId) return { success: false, message: 'Booking id and product id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products/${productId}/cancel`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to cancel product' };
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || err.message || 'Failed to cancel product';
      setError(msg);
      return { success: false, message: msg, status: err.response?.status, blockType: data?.blockType || null, rule: data?.rule || null };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/**
 * Cancel one or more directions (Outbound / Inbound) of a flight while keeping the
 * rest of the flight active. Backend POST /:id/products/:productId/cancel-legs marks
 * the direction's legs as Cancelled, drops/​prorates that direction's pricing, and
 * recalculates booking totals. Rolls the whole flight up to Cancelled only when the
 * last active direction is cancelled.
 */
export const useCancelFlightLegs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, payload = {}) => {
    if (!bookingId || !productId) return { success: false, message: 'Booking id and product id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products/${productId}/cancel-legs`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to cancel flight direction' };
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || err.message || 'Failed to cancel flight direction';
      setError(msg);
      return { success: false, message: msg, status: err.response?.status, blockType: data?.blockType || null };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

const SUPPORTED_PRODUCT_TYPES = ['Flight', 'Hotel', 'Transfer', 'Insurance', 'Package', 'CarRental', 'Visa', 'Cruise', 'Incoming'];

export const isSupportedProductType = (type) => SUPPORTED_PRODUCT_TYPES.includes(type);

export const mapUiProductToApi = (product) => {
  const base = {
    productType: product.type,
    productStatus: product.status || 'Pending',
    serviceStatus: product.serviceStatus || 'Pending',
    supplierId: product.supplierId || undefined,
    // The Supplier dropdown writes the picked name into `product.supplier` (see
    // ProductFormScreen's onChange). Without forwarding it here, editing a product
    // updates supplierId on the backend but leaves the displayed supplier NAME
    // frozen at whatever it was when the product was first created.
    supplierName: product.supplier || undefined,
    supplierCommercialRole: product.supplierCommercialRole || undefined,
    // `product.supplierRef` is a mirror of the backend's supplierReference column,
    // populated when the edit form opens (mapApiProductToForm) but never live-edited
    // by CarRental/Package ("Supplier Reservation Nr") or Visa ("Supplier Reference
    // Nr") — those forms only touch supplierReservationNumber / supplierRefNumber.
    // Checking supplierRef FIRST meant that once it held any value, it permanently
    // won over later edits to those fields, so the saved reference (and therefore
    // the displayed "Supplier Ref") could never change again after the first save.
    // Check the type-specific, actively-edited field first instead.
    supplierReference: product.supplierReservationNumber || product.supplierRefNumber || product.supplierRef || undefined,
    creationDate: product.creationDate || undefined,
    internalNotes: product.notes || undefined,
  };
  if (product._serverId) base.productId = product._serverId;

  if (product.type === 'Flight') {
    base.bookingContext = product.flightBookingMode === 'flightonly' ? 'Flight Only' : 'Flight within Travel Booking';
    base.productDetails = {
      flightType: product.flightType,
      flightPricingMode: product.flightPricingMode,
      airlinePnr: product.airlinePnr,
      ticketNumber: product.ticketNumber,
      // SPLIT pricing mode — direction-level PNR & ticket (RT / Multi)
      outboundPnr: product.outboundPnr || undefined,
      outboundTicket: product.outboundTicket || undefined,
      inboundPnr: product.inboundPnr || undefined,
      inboundTicket: product.inboundTicket || undefined,
      // Stored in chronological order (departure date + time), never in the
      // direction-grouped order the flight form edits them in.
      legs: sortFlightLegs(product.legs).map((leg) => ({
        id: leg.id || undefined,
        direction: leg.direction,
        departureAirport: leg.departure,
        departureName: leg.departureName || undefined,
        arrivalAirport: leg.arrival,
        arrivalName: leg.arrivalName || undefined,
        departureDate: leg.date,
        departureTime: leg.time,
        arrivalDate: leg.arrivalDate,
        arrivalTime: leg.arrivalTime,
        airlineCode: leg.airlineCode,
        airlineName: leg.airlineName,
        flightNumber: leg.flightNumber,
        cabinClass: leg.cabinClass,
        fareClass: leg.fareClass,
        fareClassName: leg.fareClassName || undefined,
        fareClassId: leg.fareClassId || undefined,
        flightStatus: leg.flightStatus,
        pnr: leg.pnr,
        ticketNumber: leg.ticketNumber,
        baggage: {
          handbag: leg.baggageHandbag ? { kg: leg.baggageHandbagKg, pc: leg.baggageHandbagPc } : null,
          cabin: leg.baggageCabin ? { kg: leg.baggageCabinKg, pc: leg.baggageCabinPc } : null,
          checked: leg.baggageChecked ? { kg: leg.baggageCheckedKg, pc: leg.baggageCheckedPc } : null,
          checkedInfant: leg.baggageCheckedInfant ? { kg: leg.baggageCheckedInfantKg, pc: leg.baggageCheckedInfantPc } : null,
        },
      })),
    };
  } else if (product.type === 'Hotel') {
    base.productDetails = {
      // Whether a transfer can be booked with this hotel (default true).
      hotelContainsTransfer: product.hotelContainsTransfer !== false,
      hotelCode: product.hotelCode || undefined,
      hotelName: product.hotelName || undefined,
      hotelId: product.hotelId || undefined,
      hotelAddress: product.hotelAddress || undefined,
      // Hotel metadata from search selection — needed so edit screen restores stars/city/country
      hotelStars: product.hotelStars || undefined,
      hotelCity: product.hotelCity || undefined,
      hotelCountry: product.hotelCountry || undefined,
      checkIn: product.checkIn,
      checkOut: product.checkOut,
      nights: product.nights,
      days: product.days,
      lastPaymentDeadlineDate: product.lastPaymentDate,
      lastPaymentDeadlineTime: product.lastPaymentTime,
      lastFreeCancellationDate: product.lastFreeCancellationDate,
      lastFreeCancellationTime: product.lastFreeCancellationTime,
      firstEBPaymentPercentage: product.ebFirst_unit === '%' ? product.ebFirst_value : undefined,
      secondEBPaymentPercentage: product.ebSecond_unit === '%' ? product.ebSecond_value : undefined,
      thirdEBPaymentPercentage: product.ebThird_unit === '%' ? product.ebThird_value : undefined,
      lastDateFor1stEBPayment: product.ebFirst_date,
      lastDateFor2ndEBPayment: product.ebSecond_date,
      lastDateFor3rdEBPayment: product.ebThird_date,
      roomRate: product.roomRate,
      roomName: product.roomName,
      roomCharacteristic: product.roomCharacteristic,
      roomCode: product.roomCode,
      roomType: product.roomType,
      numberOfRooms: product.numberOfRooms,
      roomStatus: product.roomStatus,
      boardCode: product.boardCode,
      boardName: product.boardName,
      rooms: product.rooms || [],
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'Transfer') {
    base.bookingContext = product.transferContext === 'standalone' ? 'Standalone Transfer' : 'Transfer within Travel Booking';

    const mapLeg = (leg) => ({
      schedule: {
        pickupLocationType: leg.pickupLocationType,
        dropoffLocationType: leg.dropoffLocationType,
        pickupLocation: leg.pickupLocation,
        dropoffLocation: leg.dropoffLocation,
        pickupDate: leg.pickupDate,
        pickupTimeType: leg.pickupTimeType || "exact",
        pickupTime: leg.pickupTime,
      },
      flightLink: (leg.flightAirlineCode || leg._linkedFlightId) ? {
        airlineCode: leg.flightAirlineCode,
        flightNumber: leg.flightFlightNumber,
        date: leg.flightDate,
        time: leg.flightTime,
        linkedFlightId: leg._linkedFlightId || null,
        linkedLegId: leg._linkedLegId || null,
      } : null,
      pickupInstructions: leg.pickupInstructions,
      cantFindInstructions: leg.cantFindInstructions,
      travelDisruptionInfo: leg.travelDisruptionInfo,
      contactInfoSupplier: leg.contactInfoSupplier,
    });

    if (product.transferMoment === 'RoundTrip') {
      base.productDetails = {
        transferMoment: product.transferMoment,
        transferType: product.transferType,
        vehicleType: product.vehicleType,
        passengers: {
          adults: product.numberOfAdults,
          children: product.numberOfChildren,
          leadTravelerName: product.leadTravelerName,
        },
        arrivalLeg: mapLeg(product.arrivalLeg || {}),
        departureLeg: mapLeg(product.departureLeg || {}),
        travelDisruptionInfo: product.travelDisruptionInfo,
        contactInfoSupplier: product.contactInfoSupplier,
        emergencyAssistance: product.emergencyAssistance,
        clientRemarks: product.clientRemarks,
        supplierRemarks: product.supplierRemarks,
        officeNotes: product.officeNotes,
      };
    } else {
      base.productDetails = {
        transferMoment: product.transferMoment,
        transferType: product.transferType,
        vehicleType: product.vehicleType,
        schedule: {
          pickupLocationType: product.pickupLocationType,
          dropoffLocationType: product.dropoffLocationType,
          pickupLocation: product.pickupLocation,
          dropoffLocation: product.dropoffLocation,
          pickupDate: product.pickupDate,
          pickupTimeType: product.pickupTimeType || "exact",
          pickupTime: product.pickupTime,
        },
        passengers: {
          adults: product.numberOfAdults,
          children: product.numberOfChildren,
          leadTravelerName: product.leadTravelerName,
        },
        flightLink: (product.flightAirlineCode || product._linkedFlightId) ? {
          airlineCode: product.flightAirlineCode,
          flightNumber: product.flightFlightNumber,
          date: product.flightDate,
          time: product.flightTime,
          linkedFlightId: product._linkedFlightId || null,
          linkedLegId: product._linkedLegId || null,
        } : null,
        pickupInstructions: product.pickupInstructions,
        cantFindInstructions: product.cantFindInstructions,
        travelDisruptionInfo: product.travelDisruptionInfo,
        contactInfoSupplier: product.contactInfoSupplier,
        emergencyAssistance: product.emergencyAssistance,
        clientRemarks: product.clientRemarks,
        supplierRemarks: product.supplierRemarks,
        officeNotes: product.officeNotes,
      };
    }
  } else if (product.type === 'Insurance') {
    base.productDetails = {
      insuranceContext: product.insuranceContext,
      insuranceType: product.insuranceType,
      insuranceName: product.insuranceName,
      insuranceIdNumber: product.insuranceIdNumber,
      coverageArea: product.coverageArea,
      insuranceStartDate: product.insuranceStartDate,
      holidayStartDate: product.holidayStartDate,
      insuranceExpireDate: product.insuranceExpireDate,
      numberOfPersons: product.numberOfPersons,
      bookingDate: product.bookingDate,
      policyType: product.policyType,
      coverage: product.coverage,
      validFrom: product.validFrom,
      validTo: product.validTo,
      policyRef: product.policyRef,
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      invoiceNotes: product.invoiceNotes,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'Package') {
    base.productDetails = {
      packageType: product.packageType,
      supplierReservationNumber: product.supplierReservationNumber,
      supplierInvoiceNumber: product.supplierInvoiceNumber,
      bookingDate: product.bookingDate,
      packageName: product.packageName,
      destinationCity: product.destinationCity,
      destinationCountry: product.destinationCountry,
      destination: product.destination || product.destinationCity,
      departureDate: product.departureDate,
      departure: product.departure || product.departureDate,
      returnDate: product.return || product.returnDate,
      numberOfNights: product.numberOfNights,
      passengers: product.passengers,
      pkgHotelName: product.pkgHotelName,
      pkgHotelStars: product.pkgHotelStars,
      pkgHotelCity: product.pkgHotelCity,
      pkgHotelCountry: product.pkgHotelCountry,
      pkgNumberOfAdults: product.pkgNumberOfAdults,
      pkgNumberOfChildren: product.pkgNumberOfChildren,
      pkgNumberOfRooms: product.pkgNumberOfRooms,
      pkgRoomType: product.pkgRoomType,
      pkgRoomName: product.pkgRoomName,
      pkgRoomCharacteristics: product.pkgRoomCharacteristics,
      pkgBoardType: product.pkgBoardType,
      boardType: product.boardType || product.pkgBoardType,
      pkgDepartureAirport: product.pkgDepartureAirport,
      pkgArrivalAirport: product.pkgArrivalAirport,
      pkgAirlineName: product.pkgAirlineName,
      pkgTransferIncluded: product.pkgTransferIncluded,
      pkgTransferPriceIncluded: product.pkgTransferPriceIncluded,
      pkgInsuranceIncluded: product.pkgInsuranceIncluded,
      pkgInsurancePriceIncluded: product.pkgInsurancePriceIncluded,
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'CarRental') {
    base.productDetails = {
      carRentalContext: product.carRentalContext,
      supplierReservationNumber: product.supplierReservationNumber,
      bookingDate: product.bookingDate,
      carCategory: product.carCategory,
      vehicleMakeModel: product.vehicleMakeModel,
      orSimilar: product.orSimilar,
      vehicleType: product.vehicleType,
      pickupLocation: product.pickupLocation,
      dropoffLocation: product.dropoffLocation,
      pickupDate: product.pickupDate,
      pickupTime: product.pickupTime,
      dropoffDate: product.dropoffDate,
      dropoffTime: product.dropoffTime,
      rentalDays: product.rentalDays,
      creditCardRequired: product.creditCardRequired,
      depositRequired: product.depositRequired,
      insuranceIncluded: product.insuranceIncluded,
      leadDriverName: product.leadDriverName,
      leadDriverAge: product.leadDriverAge,
      additionalDrivers: product.additionalDrivers,
      driver: product.driver || product.leadDriverName,
      pickupInstructions: product.pickupInstructions,
      supplierContactInfo: product.supplierContactInfo,
      emergencyAssistance: product.emergencyAssistance,
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      invoiceNotes: product.invoiceNotes,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'Visa') {
    base.productDetails = {
      visaContext: product.visaContext,
      supplierRefNumber: product.supplierRefNumber,
      bookingDate: product.bookingDate,
      visaType: product.visaType,
      visaCountry: product.visaCountry,
      destinationCountry: product.destinationCountry || product.visaCountry,
      visaEntryType: product.visaEntryType,
      visaDecisionStatus: product.visaDecisionStatus,
      visaIssueDate: product.visaIssueDate,
      visaExpiryDate: product.visaExpiryDate,
      visaDurationOfStay: product.visaDurationOfStay,
      travelers: product.travelers,
      applicationDate: product.applicationDate,
      visaStatus: product.visaStatus || product.visaDecisionStatus,
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'Cruise') {
    // CruiseForm keeps its fields at the top level of the form object, the way
    // every other product form does. Spreading productDetails alone dropped all
    // of them on save, so a cruise persisted with nothing but its remarks.
    base.productDetails = {
      ...(product.productDetails || {}),
      cruiseContext: product.cruiseContext,
      bookingDate: product.bookingDate,
      cruiseLine: product.cruiseLine,
      shipName: product.shipName,
      cruiseNameItinerary: product.cruiseNameItinerary,
      departurePort: product.departurePort,
      arrivalPort: product.arrivalPort,
      departureDate: product.departureDate,
      returnDate: product.returnDate,
      duration: product.duration,
      cabins: product.cabins,
      portsOfCall: product.portsOfCall,
      numberOfAdults: product.numberOfAdults,
      numberOfChildren: product.numberOfChildren,
      leadTravelerName: product.leadTravelerName,
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      officeNotes: product.officeNotes,
    };
  } else if (product.type === 'Incoming') {
    base.productDetails = {
      ...(product.productDetails || {}),
      clientRemarks: product.clientRemarks,
      supplierRemarks: product.supplierRemarks,
      officeNotes: product.officeNotes,
    };
  }
  return base;
};

export const useCreateBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        customerId: payload.customerId,
        customerType: payload.customerType,
        customerName: payload.customerName,
        customerEmail: payload.customerEmail,
        customerPhone: payload.customerPhone,
        bookingType: payload.bookingType || 'Offline',
        internalNotes: payload.internalNotes,
      };
      const response = await axiosInstance.post(BOOKING_BASE, apiPayload);
      const res = response.data;
      if (res?.success && res?.data) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to create booking' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create booking';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useAddBookingProducts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, uiProducts) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const products = (uiProducts || [])
        .filter((p) => isSupportedProductType(p.type))
        .map(mapUiProductToApi);
      if (products.length === 0) {
        return { success: false, message: 'No supported products to save' };
      }
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products`, { products });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data || [] };
      return { success: false, message: res?.message || 'Failed to save products' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save products';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useUpdateBookingProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, uiProduct) => {
    if (!bookingId || !productId) return { success: false, message: 'Booking id and product id are required' };
    setLoading(true);
    setError(null);
    try {
      const apiData = mapUiProductToApi(uiProduct);
      delete apiData.productId;
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/products/${productId}`, apiData);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update product' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update product';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useDeleteBookingProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId) => {
    if (!bookingId || !productId) return { success: false, message: 'Booking id and product id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/products/${productId}`);
      const res = response.data;
      if (res?.success !== false) return { success: true };
      return { success: false, message: res?.message || 'Failed to delete product' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete product';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useAddBookingTravelers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, uiTravelers) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const travelers = (uiTravelers || []).map((t) => ({
        ...(t._serverId ? { travelerId: t._serverId } : {}),
        firstName: t.firstName,
        lastName: t.lastName,
        gender: t.gender,
        dateOfBirth: t.dob,
      }));
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/travelers`, { travelers });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data || [] };
      return { success: false, message: res?.message || 'Failed to save travelers' };
    } catch (err) {
      const data = err.response?.data;
      const detail = data?.error && data.error !== data?.message ? `: ${data.error}` : '';
      const msg = (data?.message || err.message || 'Failed to save travelers') + detail;
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useDeleteBookingTraveler = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, travelerId) => {
    if (!bookingId || !travelerId) return { success: false, message: 'Booking id and traveler id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/travelers/${travelerId}`);
      const res = response.data;
      if (res?.success !== false) return { success: true };
      return { success: false, message: res?.message || 'Failed to delete traveler' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete traveler';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useCreateBookingAssignments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, apiAssignments, extra) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    if (!apiAssignments?.length) return { success: false, message: 'No assignments to save' };
    setLoading(true);
    setError(null);
    try {
      const body = { assignments: apiAssignments };
      if (extra?.adminUnlockReason) body.adminUnlockReason = extra.adminUnlockReason;
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/assignments`, body);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data || [] };
      return { success: false, message: res?.message || 'Failed to save assignments', assignmentLocked: res?.assignmentLocked, lockReasons: res?.lockReasons };
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.message || err.message || 'Failed to save assignments';
      setError(msg);
      return { success: false, message: msg, assignmentLocked: errData?.assignmentLocked, lockReasons: errData?.lockReasons };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useDeleteBookingAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, assignmentId, config) => {
    if (!bookingId || !assignmentId) return { success: false, message: 'Booking id and assignment id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/assignments/${assignmentId}`, config);
      const res = response.data;
      if (res?.success !== false) return { success: true };
      return { success: false, message: res?.message || 'Failed to delete assignment', assignmentLocked: res?.assignmentLocked, lockReasons: res?.lockReasons };
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.message || err.message || 'Failed to delete assignment';
      setError(msg);
      return { success: false, message: msg, assignmentLocked: errData?.assignmentLocked, lockReasons: errData?.lockReasons };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

// Update an assignment's room / seat (metadata only — not an unlink, so it is
// not subject to the finance/unlink lock). Used for the per-room hotel guest
// assignment in the booking detail.
export const useUpdateBookingAssignment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, assignmentId, updates) => {
    if (!bookingId || !assignmentId) return { success: false, message: 'Booking id and assignment id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BOOKING_BASE}/${bookingId}/assignments/${assignmentId}`, updates || {});
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update assignment' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update assignment';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useEditBookingTraveler = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, travelerId, payload) => {
    if (!bookingId || !travelerId) return { success: false, message: 'Booking id and traveler id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/travelers/${travelerId}`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update traveler' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update traveler';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useCreateTravelerModification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, travelerId, payload) => {
    if (!bookingId || !travelerId) return { success: false, message: 'Booking id and traveler id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/travelers/${travelerId}/modifications`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to apply modification' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to apply modification';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   STEP 5 — SSRs
   ────────────────────────────────────────────────────────────────────────── */
export const useAddBookingSSRs = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, apiSsrs) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    if (!apiSsrs?.length) return { success: false, message: 'No SSRs to save' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/ssrs`, { ssrs: apiSsrs });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data || [] };
      return { success: false, message: res?.message || 'Failed to save SSRs' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save SSRs';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useDeleteBookingSSR = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, ssrId) => {
    if (!bookingId || !ssrId) return { success: false, message: 'Booking id and ssr id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/ssrs/${ssrId}`);
      const res = response.data;
      if (res?.success !== false) return { success: true };
      return { success: false, message: res?.message || 'Failed to delete SSR' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete SSR';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

// Update only the status of an existing SSR (Requested / Confirmed / Cancelled / Denied).
export const useUpdateSSRStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, ssrId, status) => {
    if (!bookingId || !ssrId) return { success: false, message: 'Booking id and ssr id are required' };
    if (!status) return { success: false, message: 'status is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/ssrs/${ssrId}/status`, { status });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update SSR status' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update SSR status';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

// Update only the free-text detail (ssrDescription) of an existing SSR.
// This is the grey "detail" text shown on the SSR row and in the Detail
// column of the flight voucher (e.g. a seat number or short note).
export const useUpdateSSRDetails = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, ssrId, payload) => {
    if (!bookingId || !ssrId) return { success: false, message: 'Booking id and ssr id are required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/ssrs/${ssrId}/details`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to update SSR detail' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update SSR detail';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   STEP 6 — Pricing
   ────────────────────────────────────────────────────────────────────────── */
const usePricingMutation = (path, errorLabel) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, payload) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/pricing/${path}`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || `Failed to save ${errorLabel} pricing` };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || `Failed to save ${errorLabel} pricing`;
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, [path, errorLabel]);

  return { execute, loading, error, clearError };
};

export const useSetFlightPricing = () => usePricingMutation('flight', 'flight');
export const useSetHotelPricing = () => usePricingMutation('hotel', 'hotel');
export const useSetTransferPricing = () => usePricingMutation('transfer', 'transfer');
export const useSetSSRPricing = () => usePricingMutation('ssr', 'SSR');
export const useSetGenericPricing = () => usePricingMutation('generic', 'generic product');

export const useGetBookingPricing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/pricing`);
      const res = response.data;
      if (res?.success) {
        setData(res.data);
        return { success: true, data: res.data };
      }
      return { success: false, message: res?.message || 'Failed to fetch pricing' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch pricing';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, data, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   Adjustments (Discounts & Surcharges)
   ────────────────────────────────────────────────────────────────────────── */

export const useSetAdjustments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, adjustments) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BOOKING_BASE}/${bookingId}/adjustments`, { adjustments });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to save adjustments' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save adjustments';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useGetAdjustments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/adjustments`);
      const res = response.data;
      if (res?.success) {
        setData(res.data);
        return { success: true, data: res.data };
      }
      return { success: false, message: res?.message || 'Failed to fetch adjustments' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch adjustments';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, data, loading, error };
};

/* ──────────────────────────────────────────────────────────────────────────
   STEP 7 — Submit Booking (Draft → Pending)
   Called by the "Create Booking" button at the end of the new-booking wizard.
   ────────────────────────────────────────────────────────────────────────── */
export const useSubmitBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BOOKING_BASE}/${bookingId}/submit`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to submit booking' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to submit booking';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   Confirm Booking (Pending → Confirmed)
   Used from the BookingDetail page by an authorised manager/admin.
   NOT used in the create-booking wizard.
   ────────────────────────────────────────────────────────────────────────── */
export const useConfirmBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BOOKING_BASE}/${bookingId}/confirm`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to confirm booking' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to confirm booking';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   VAT Calculation
   ────────────────────────────────────────────────────────────────────────── */
export const useCalculateVAT = () => {
  const execute = useCallback(async (payload) => {
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/vat/calculate`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'VAT calculation failed' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'VAT calculation failed' };
    }
  }, []);

  return { execute };
};

/* ──────────────────────────────────────────────────────────────────────────
   Itinerary: Copy Product
   ────────────────────────────────────────────────────────────────────────── */
export const useCopyProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products/${productId}/copy`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to copy product' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to copy product';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

/* ──────────────────────────────────────────────────────────────────────────
   Itinerary: Validate Product Removal
   ────────────────────────────────────────────────────────────────────────── */
export const useValidateProductRemoval = () => {
  const execute = useCallback(async (bookingId, productId) => {
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/products/${productId}/validate-removal`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to validate removal' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Validation failed' };
    }
  }, []);

  return { execute };
};

/* ──────────────────────────────────────────────────────────────────────────
   Itinerary: Product Modifications (Post-Booking)
   ────────────────────────────────────────────────────────────────────────── */
export const useCreateProductModification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products/${productId}/modifications`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to create modification' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create modification';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useGetProductModifications = () => {
  const execute = useCallback(async (bookingId, productId) => {
    try {
      const url = productId
        ? `${BOOKING_BASE}/${bookingId}/products/${productId}/modifications`
        : `${BOOKING_BASE}/${bookingId}/modifications`;
      const response = await axiosInstance.get(url);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to fetch modifications' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to fetch modifications' };
    }
  }, []);

  return { execute };
};

// Reverse (delete) a post-booking modification. Super-admin only on the backend.
export const useDeleteProductModification = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (bookingId, modificationId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/modifications/${modificationId}`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      return { success: false, message: res?.message || 'Failed to remove modification' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to remove modification' };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};

export const useDeleteTravelerModification = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (bookingId, modificationId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.delete(`${BOOKING_BASE}/${bookingId}/traveler-modifications/${modificationId}`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      return { success: false, message: res?.message || 'Failed to remove modification' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to remove modification' };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
};

/* ──────────────────────────────────────────────────────────────────────────
   Cancellation case (Cancellation Engine "Confirm" persistence)
   ────────────────────────────────────────────────────────────────────────── */
export const useCreateCancellationCase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, payload) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/cancellation-cases`, payload);
      const res = response.data;
      if (res?.success) {
        return {
          success: true,
          data: res.data,
          documentNumber: res.documentNumber,
          // Auto-created pending refund records (SUNSKY cancellation financial flow)
          clientRefund: res.clientRefund || null,
          supplierRefund: res.supplierRefund || null,
          warnings: res.warnings || [],
        };
      }
      return { success: false, message: res?.message || 'Failed to record cancellation case' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to record cancellation case';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useGetCancellationCases = () => {
  const execute = useCallback(async (bookingId) => {
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/cancellation-cases`);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to fetch cancellation cases' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to fetch cancellation cases' };
    }
  }, []);

  return { execute };
};

/* ──────────────────────────────────────────────────────────────────────────
   Itinerary: Schedule Changes
   ────────────────────────────────────────────────────────────────────────── */
export const useCreateScheduleChange = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (bookingId, productId, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BOOKING_BASE}/${bookingId}/products/${productId}/schedule-changes`, payload);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to record schedule change' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to record schedule change';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
};

export const useGetScheduleChanges = () => {
  const execute = useCallback(async (bookingId, productId) => {
    try {
      const url = productId
        ? `${BOOKING_BASE}/${bookingId}/products/${productId}/schedule-changes`
        : `${BOOKING_BASE}/${bookingId}/schedule-changes`;
      const response = await axiosInstance.get(url);
      const res = response.data;
      if (res?.success) return { success: true, data: res.data };
      return { success: false, message: res?.message || 'Failed to fetch schedule changes' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to fetch schedule changes' };
    }
  }, []);

  return { execute };
};

export const useGetAssignmentLockStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (bookingId) => {
    if (!bookingId) return { success: false, message: 'Booking id is required' };
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${BOOKING_BASE}/${bookingId}/assignments/lock-status`);
      const res = response.data;
      if (res?.success) {
        setData(res.data);
        return { success: true, data: res.data };
      }
      return { success: false, message: res?.message || 'Failed to fetch lock status' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to fetch lock status' };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, data, loading };
};

/**
 * useLookupFlightBaggage
 *
 * Baggage allowance for flight legs from Airtuerk's fare data, for the Add
 * Booking flight form. Takes wizard-shaped legs ({ direction, departure,
 * arrival, date, airlineCode, flightNumber, fareClass }) and returns
 * { success, message, legs: [{ index, found, ambiguous, fareName, baggage }], warnings }.
 * Never throws — a failure comes back as success:false with a message.
 */
export const useLookupFlightBaggage = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (legs, counts = {}) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        '/flights/baggage-lookup',
        { legs, ...counts },
        { timeout: 90000 }
      );
      return {
        success: Boolean(response.data?.success),
        message: response.data?.message || '',
        legs: response.data?.data?.legs || [],
        warnings: response.data?.data?.warnings || [],
      };
    } catch (err) {
      return {
        success: false,
        message: err?.response?.data?.message || err?.message || 'Baggage lookup failed',
        legs: [],
        warnings: [],
      };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

/**
 * useCheckDuplicateReference
 *
 * Real-time duplicate check for supplier references and airline PNRs.
 * Returns { isDuplicate, count, bookings } — never throws, falls back to no-duplicate on error.
 *
 * type: 'supplier_ref' | 'airline_pnr' | 'outbound_pnr' | 'inbound_pnr'
 */
export const useCheckDuplicateReference = () => {
  const execute = useCallback(async (type, value, excludeBookingId = null) => {
    const trimmed = value?.trim();
    if (!trimmed || trimmed.length < 3) return { isDuplicate: false, count: 0, bookings: [] };
    try {
      const params = { type, value: trimmed };
      if (excludeBookingId) params.excludeBookingId = excludeBookingId;
      const response = await axiosInstance.get(`${BOOKING_BASE}/check-duplicate`, { params });
      return response.data?.data ?? { isDuplicate: false, count: 0, bookings: [] };
    } catch {
      return { isDuplicate: false, count: 0, bookings: [] };
    }
  }, []);

  return { execute };
};
